import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { FEATURES } from '../constants/features'
import { useConfigStore } from './configStore'

const API = `${import.meta.env.VITE_CLIENT_API_URL}`.replace(/\/$/, '')

export const useFavoritesStore = defineStore('favorites', () => {
  const configStore = useConfigStore()
  const ids = ref(new Set())   // Set of content IDs
  const items = ref({})        // id compact video object
  const loading = ref(false)
  const favorites = computed(() => {
    if (ids.value.size) {
      return [...ids.value].map(id => items.value[id]).filter(Boolean)
    }
    return []
  })


  function isFavorited(videoId) {
    return ids.value.has(videoId)
  }

  function compact(video) {
    return {
      id: video.id,
      title: video.title,
      thumbnail: video.thumbnail || video.thumbnail2x3,
      heroImage: video.heroImage,
      content: video.content,
      genres: video.genres,
      feedId: video.feedId || video.distributionId || video.partnerId,
    }
  }

  async function fetchServerFavorites(distributionId, userId, search = '') {
    if (!configStore.features.includes(FEATURES.REGISTRATION_FAVORITES)) {
      console.debug('Favorites feature is not enabled, skipping fetch')
      return
    }
    
    loading.value = true
    const url = new URL(`${API}/favorites-data/${distributionId}`)
    url.searchParams.set('user_id', userId)
    if (search) {
      url.searchParams.set('search', search)
    }

    const res = await fetch(url.toString())
    if (!res.ok) {
      loading.value = false
      throw new Error('Failed to fetch favorites')
    }

    const data = await res.json()
    const list = processFavoritesResponse(data)

    ids.value = new Set(list.map(v => v.id))
  
    items.value = {}
    for (const v of list) {
      const obj = compact({
        ...v,
        id: v.id || v.content_id, // normalize API shape
        feedId: distributionId,
      })
      items.value[obj.id] = obj
    }
    loading.value = false
  }

  function processFavoritesResponse(data) {
    const sections = data.sections.map(section => section.key).filter(key => key !== 'series' && key !== 'partners')

    const videos = sections.reduce((acc, section) => {
      return acc.concat(data[section])
    }, [])

    return videos
  }

  async function addServerFavorite(distributionId, userId, video) {
    const body = { user_id: userId, content_id: video.id }

    const res = await fetch(`${API}/add-favorites/${distributionId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    if (!res.ok) {
      throw new Error('Failed to add favorite')
    }

    // update UI on success
    ids.value.add(video.id)
    items.value[video.id] = compact({ ...video, feedId: distributionId })
  }

  async function removeServerFavorite(distributionId, userId, videoId) {
    const res = await fetch(`${API}/remove-favorites/${distributionId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: userId, content_id: videoId }),
    })
    if (!res.ok) {
      throw new Error('Failed to remove favorite')
    }

    // update local state
    ids.value.delete(videoId)
    delete items.value[videoId]
  }

  async function toggle(video, { userId, distributionId }) {
    if (!configStore.features.includes(FEATURES.REGISTRATION_FAVORITES)) {
      console.debug('Favorites feature is not enabled, skipping toggle')
      return ""
    }

    if (!userId) {
      console.log("Can't toggle favorites when logged out")
      return ""
    }

    if (ids.value.has(video.id)) {
      await removeServerFavorite(distributionId, userId, video.id)
      ids.value.delete(video.id)
      delete items.value[video.id]
      return "Removed from favorites"
    } else {
      await addServerFavorite(distributionId, userId, video)
      ids.value.add(video.id)
      items.value[video.id] = compact({ ...video, feedId: distributionId })
      return "Added to favorites"
    }
  }

  return {
    favorites,
    isFavorited,
    fetchServerFavorites,
    addServerFavorite,
    toggle,
    ids, // exported for quick lookups if needed
    loading,
  }
})
